import { Component, createApp, defineAsyncComponent } from "vue";

/**
 * Creates a root DOM element with the given ID if it doesn't already exist.
 * @param id - The ID of the root element.
 * @returns The root DOM element.
 */
export const createRootElement = (id: string): HTMLElement => {
    let root = document.getElementById(id);
    if (!root) {
        root = document.createElement("div");
        root.setAttribute("id", id);
        document.body.appendChild(root);
    }
    return root;
};

/**
 * Dynamically imports and mounts a Vue component.
 * @param id - The ID of the root element.
 * @param componentImportFn - The dynamic import function for the component.
 * @param mountPromise - A ref to store the mount promise.
 */
export const mountComponent = async (
    id: string,
    componentImportFn: () => Promise<Component>,
    mountPromise: { value: Promise<any> | null }
): Promise<void> => {
    if (mountPromise.value) return mountPromise.value;

    const root = createRootElement(id);

    mountPromise.value = componentImportFn();
    const component = defineAsyncComponent(() => mountPromise.value);

    const app = createApp(component);
    app.mount(root);

    await mountPromise.value;
};
